import { useQuery } from '@tanstack/react-query';
import { TriangleAlert, Users } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import {
  ATTENDANCE_STATUS_LABELS,
  PERMISSIONS,
  formatMinutes,
  type AttendanceRecordDto,
  type DailyAttendanceDto,
  type DailyAttendanceRow,
} from '@hrms/shared';
import { get } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/components/common/error-state';
import { useAuth } from '@/features/auth/use-auth';
import { PunchThumbnail } from '@/features/attendance/punch-thumbnail';
import { PunchPhotoDialog } from '@/features/attendance/punch-photo-dialog';
import { DeclinePunchDialog } from '@/features/attendance/decline-punch-dialog';

const STATUS_VARIANTS: Record<string, BadgeProps['variant']> = {
  PRESENT: 'success',
  LATE: 'warning',
  HALF_DAY: 'warning',
  REST_DAY: 'secondary',
  HOLIDAY_WORKED: 'info',
};

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-PH', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Manila',
  });
}

/** One number and what it counts. */
function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'warning' | 'destructive';
}) {
  return (
    <div className="min-w-[72px]">
      <p
        className={cn(
          'text-xl font-semibold tabular-nums',
          tone === 'warning' && 'text-warning',
          tone === 'destructive' && 'text-destructive',
        )}
      >
        {value}
      </p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

/*
  The pieces below are shared by the phone card and the desktop table so the two
  never drift apart — the same clock-in photo, the same badges, the same review
  button, laid out two ways.
*/

/** The clock-in thumbnail (or a placeholder), and who the punch belongs to. */
function PunchPerson({ row, onView }: { row: DailyAttendanceRow; onView: () => void }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      {row.photos.some((photo) => photo.kind === 'TIME_IN') ? (
        <PunchThumbnail
          recordId={row.id}
          onClick={onView}
          alt={`Clock-in photo for ${row.employeeName}`}
        />
      ) : (
        <div
          className="grid size-10 shrink-0 place-items-center rounded-md border bg-muted text-xs text-muted-foreground"
          title="No photo on this punch"
        >
          —
        </div>
      )}
      <div className="min-w-0">
        <Link
          to={`/employees/${row.employeeId}`}
          className="block truncate font-medium hover:underline"
        >
          {row.employeeName}
        </Link>
        <p className="truncate text-xs text-muted-foreground">
          {row.positionTitle || row.employeeNo}
        </p>
      </div>
    </div>
  );
}

/** In and out, with the badges that qualify the punch. */
function PunchTimeline({ row }: { row: DailyAttendanceRow }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-sm tabular-nums">
      {formatTime(row.timeIn)} –{' '}
      {row.timeOut ? formatTime(row.timeOut) : <span className="text-success">still in</span>}
      {row.lateMinutes > 0 && <Badge variant="warning">{row.lateMinutes}m late</Badge>}
      {row.declinedAt && <Badge variant="destructive">Declined</Badge>}
      {row.photos.some((photo) => photo.isClockSuspect) && (
        <Badge variant="warning" className="gap-1" title="Device clock was out">
          <TriangleAlert className="size-3" aria-hidden="true" />
          Clock
        </Badge>
      )}
    </div>
  );
}

function StatusPill({ row }: { row: DailyAttendanceRow }) {
  return (
    <Badge variant={STATUS_VARIANTS[row.status] ?? 'outline'}>
      {ATTENDANCE_STATUS_LABELS[row.status] ?? row.status}
    </Badge>
  );
}

/*
  Declining costs the employee the day's pay, so the wording stays the same as
  the register's and the confirmation dialog is what actually explains it.
*/
function ReviewButton({ row, onReview }: { row: DailyAttendanceRow; onReview: () => void }) {
  return (
    <Button variant={row.declinedAt ? 'outline' : 'ghost'} size="sm" onClick={onReview}>
      {row.declinedAt ? 'Restore' : 'Decline'}
      <span className="sr-only"> punch for {row.employeeName}</span>
    </Button>
  );
}

/**
 * The day's register, for anyone who oversees other people.
 *
 * Replaces the old "On duty now" card, which filtered to `timeOut: null` and so
 * emptied itself as people clocked out — by the evening it read "nobody is
 * clocked in" on a day the whole company had worked.
 */
export function AttendanceTodayCard() {
  const [tab, setTab] = useState<'present' | 'absent'>('present');
  const [viewing, setViewing] = useState<AttendanceRecordDto | null>(null);
  const [reviewing, setReviewing] = useState<AttendanceRecordDto | null>(null);

  // The same pair the register gates its Decline column on: managers act
  // through approve-correction, HR and admin through manage.
  const { canAny } = useAuth();
  const canReview = canAny(
    PERMISSIONS.ATTENDANCE_MANAGE,
    PERMISSIONS.ATTENDANCE_APPROVE_CORRECTION,
  );

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['attendance', 'daily'],
    queryFn: () => get<DailyAttendanceDto>('/attendance/daily'),
    // A glance-at panel, not a live feed.
    refetchInterval: 60_000,
  });

  const present = data?.present ?? [];
  const absent = data?.absent ?? [];
  const summary = data?.summary;

  return (
    <Card>
      <CardHeader className="gap-3 pb-3">
        <div className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="size-4 text-muted-foreground" aria-hidden="true" />
            Attendance today
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/attendance">Full register</Link>
          </Button>
        </div>

        {summary && (
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Stat label="In today" value={summary.present} />
            <Stat label="Still on the clock" value={summary.stillIn} />
            <Stat
              label="Late"
              value={summary.late}
              tone={summary.late > 0 ? 'warning' : undefined}
            />
            <Stat
              label="Absent"
              value={summary.absent}
              tone={summary.absent > 0 ? 'destructive' : undefined}
            />
            <Stat label="On leave" value={summary.onLeave} />
            <Stat label="Expected in" value={summary.expected} />
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        {/*
          Two tabs rather than one long list. "Who is missing" and "who is here"
          are different questions asked at different times of day, and answering
          both in one table means neither is quick to read.
        */}
        <div className="flex gap-1 rounded-md border p-0.5" role="tablist">
          {(
            [
              ['present', `Present (${present.length})`],
              ['absent', `Not in (${absent.length})`],
            ] as const
          ).map(([key, label]) => (
            <Button
              key={key}
              role="tab"
              aria-selected={tab === key}
              variant="ghost"
              size="sm"
              className={cn('flex-1', tab === key && 'bg-accent text-accent-foreground')}
              onClick={() => setTab(key)}
            >
              {label}
            </Button>
          ))}
        </div>

        {/*
          The error branch has to come first, because the one below it is a
          sentence about the workforce. A failed request leaves the list empty
          and the card then says "Nobody has clocked in yet today" — to a
          supervisor, at the start of a shift, on the dashboard. That is not a
          blank state, it is a false statement.
        */}
        {isError ? (
          <ErrorState
            error={error}
            onRetry={() => void refetch()}
            title="Could not load today's register"
            className="px-4 py-6"
          />
        ) : isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-12 w-full rounded-md" />
            ))}
          </div>
        ) : tab === 'present' ? (
          present.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nobody has clocked in yet today.</p>
          ) : (
            <>
              {/*
                On a phone this is a stacked card per person, the way the rest of
                the app lists rows. The old sideways-scrolling table put half the
                register off the edge of the screen on the very first thing a
                supervisor sees after signing in.
              */}
              <ul className="divide-y sm:hidden">
                {present.map((row) => (
                  <li key={row.id} className="space-y-2 py-3 first:pt-0 last:pb-0">
                    <div className="flex items-start justify-between gap-3">
                      <PunchPerson row={row} onView={() => setViewing(row)} />
                      <StatusPill row={row} />
                    </div>
                    <PunchTimeline row={row} />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {row.workedMinutes > 0 ? formatMinutes(row.workedMinutes) : '—'} worked
                        {row.departmentName ? ` · ${row.departmentName}` : ''}
                      </span>
                      {canReview && <ReviewButton row={row} onReview={() => setReviewing(row)} />}
                    </div>
                  </li>
                ))}
              </ul>

              {/* Wider than a phone: the full register as a table. */}
              <div className="hidden overflow-x-auto sm:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="pb-2 font-medium">Employee</th>
                      <th className="pb-2 font-medium">In / out</th>
                      <th className="pb-2 font-medium">Worked</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium">Department</th>
                      {canReview && (
                        <th
                          className="pb-2 text-right font-medium"
                          title="Declining a punch counts the day as an absence in payroll"
                        >
                          Review
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {present.map((row) => (
                      <tr key={row.id} className="border-b last:border-0">
                        <td className="py-2">
                          <PunchPerson row={row} onView={() => setViewing(row)} />
                        </td>
                        <td className="py-2 align-middle">
                          <PunchTimeline row={row} />
                        </td>
                        <td className="py-2 align-middle tabular-nums text-muted-foreground">
                          {row.workedMinutes > 0 ? formatMinutes(row.workedMinutes) : '—'}
                        </td>
                        <td className="py-2 align-middle">
                          <StatusPill row={row} />
                        </td>
                        <td className="py-2 align-middle text-muted-foreground">
                          {row.departmentName || '—'}
                        </td>
                        {canReview && (
                          <td className="py-2 text-right align-middle">
                            <ReviewButton row={row} onReview={() => setReviewing(row)} />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )
        ) : absent.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Everybody due in today has clocked in. People whose rest day this is are not counted as
            missing.
          </p>
        ) : (
          <>
            {/* Phones: a stacked card per person. */}
            <ul className="divide-y sm:hidden">
              {absent.map((row) => (
                <li key={row.employeeId} className="space-y-1 py-3 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link
                        to={`/employees/${row.employeeId}`}
                        className="block truncate font-medium hover:underline"
                      >
                        {row.employeeName}
                      </Link>
                      <p className="truncate text-xs text-muted-foreground">
                        {row.positionTitle || row.employeeNo}
                      </p>
                    </div>
                    {row.disposition === 'ON_LEAVE' ? (
                      <Badge variant="info">On leave</Badge>
                    ) : (
                      <Badge variant="destructive">No punch</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {row.scheduleName ?? '—'}
                    {row.departmentName ? ` · ${row.departmentName}` : ''}
                  </p>
                </li>
              ))}
            </ul>

            {/* Wider than a phone: the table. */}
            <div className="hidden overflow-x-auto sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="pb-2 font-medium">Employee</th>
                    <th className="pb-2 font-medium">Why</th>
                    <th className="pb-2 font-medium">Schedule</th>
                    <th className="pb-2 font-medium">Department</th>
                  </tr>
                </thead>
                <tbody>
                  {absent.map((row) => (
                    <tr key={row.employeeId} className="border-b last:border-0">
                      <td className="py-2">
                        <Link
                          to={`/employees/${row.employeeId}`}
                          className="block truncate font-medium hover:underline"
                        >
                          {row.employeeName}
                        </Link>
                        <p className="truncate text-xs text-muted-foreground">
                          {row.positionTitle || row.employeeNo}
                        </p>
                      </td>
                      <td className="py-2 align-middle">
                        {row.disposition === 'ON_LEAVE' ? (
                          <Badge variant="info">On leave</Badge>
                        ) : (
                          <Badge variant="destructive">No punch</Badge>
                        )}
                      </td>
                      <td className="py-2 align-middle text-muted-foreground">
                        {row.scheduleName ?? '—'}
                      </td>
                      <td className="py-2 align-middle text-muted-foreground">
                        {row.departmentName || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>

      {/*
        The register's own dialogs, not copies. Declining invalidates the
        `attendance` key, which this card's `['attendance', 'daily']` query
        sits under, so the row updates without a manual refetch.
      */}
      <PunchPhotoDialog record={viewing} onOpenChange={(open) => !open && setViewing(null)} />
      <DeclinePunchDialog record={reviewing} onOpenChange={(open) => !open && setReviewing(null)} />
    </Card>
  );
}
